import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { BehaviorSubject, filter, firstValueFrom, Subject, takeUntil } from 'rxjs';
import { UserDTO, UsersService } from '../../openapi';
import { hideAppSplash } from '../splash';
import { SignalRService } from '../../services/signal-r.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private _cUser: AccountInfo | null = null;
  private _appUser: UserDTO | null = null;
  private _appUserObjectId: string | null = null;
  private _loadAppUserPromise: Promise<UserDTO | null> | null = null;

  private readonly _appUser$ = new BehaviorSubject<UserDTO | null>(null);
  readonly appUser$ = this._appUser$.asObservable();

  private readonly _destroying$ = new Subject<void>();
  private readonly _msalAuthService = inject(MsalService);
  private readonly _msalBroadcastService = inject(MsalBroadcastService);
  private readonly _userService = inject(UsersService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _signalR = inject(SignalRService);

  private _listenersRegistered = false;

  constructor(private readonly _msal: MsalService) {}

  getCurrentUser(): AccountInfo | null {
    if (this._cUser) {
      return this._cUser;
    }

    this._cUser = this._msal.instance.getActiveAccount();
    return this._cUser;
  }

  getCurrentAppUser(): UserDTO | null {
    return this._appUser;
  }

  getCurrentUserRoles(): string[] {
    const account = this.getCurrentUser();
    if (account && account.idTokenClaims && account.idTokenClaims['roles']) {
      return account.idTokenClaims['roles'];
    }
    return [];
  }

  logout() {
    this._cUser = null;
    this._clearAppUser();
    this._msal.logout();
  }

  isAuthenticated(): boolean {
    return !!this.getCurrentUser();
  }

  async initializeAuthentication(): Promise<void> {
    try {
      await firstValueFrom(this._msalAuthService.initialize());
      await firstValueFrom(this._msalAuthService.handleRedirectObservable());
      this.checkAndSetActiveAccount();
      await this.loadAppUser();
      await this._startListeningForUpdates();
    } catch (error) {
      console.error('Authentication initialization failed', error);
      this._clearAppUser();
    } finally {
      hideAppSplash();
    }
  }

  initMsal() {
    this._msalAuthService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this._msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((_: EventMessage) => {
        if (this._msalAuthService.instance.getAllAccounts().length === 0) {
          this._clearAppUser();
          window.location.pathname = '/';
        }
      });

    this._msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });
  }

  checkAndSetActiveAccount() {
    let activeAccount = this._msalAuthService.instance.getActiveAccount();

    if (!activeAccount && this._msalAuthService.instance.getAllAccounts().length > 0) {
      let accounts = this._msalAuthService.instance.getAllAccounts();
      this._msalAuthService.instance.setActiveAccount(accounts[0]);
      activeAccount = accounts[0];
    }

    this._cUser = activeAccount;

    if (activeAccount) {
      void this.loadAppUser();
      void this._startListeningForUpdates();
    } else {
      this._clearAppUser();
    }
  }

  hasUserAnyRole(): boolean {
    if (!this._appUser || !this._appUser.isActive) {
      return false;
    }
    const roles = this._appUser?.roles ?? [];
    return roles.length > 0;
  }

  isUserInRole(roleName: string): boolean {
    if (!this._appUser || !this._appUser.isActive) {
      return false;
    }
    const roles = this._appUser?.roles ?? [];
    return roles.some((role) => role.name === roleName);
  }

  hasUserAnyPermission(): boolean {
    if (!this._appUser || !this._appUser.isActive) {
      return false;
    }
    const permissions = this._appUser?.permissions ?? [];
    return permissions.length > 0;
  }

  hasUserPermission(permissionName: string): boolean {
    if (!this._appUser || !this._appUser.isActive) {
      return false;
    }
    const permissions = this._appUser?.permissions ?? [];
    return permissions.some((permission) => permission.name === permissionName);
  }

  hasUserCalculationPermission(
    permissionName: string,
    legalEntityId?: number,
    divisionId?: number,
    productGroupId?: number
  ): boolean {
    if (!this.hasUserPermission(permissionName)) {
      return false;
    }

    return (
      this._hasAccessTo(this._appUser?.userLegalEntityAccesses, (access) => access.legalEntityId, legalEntityId) &&
      this._hasAccessTo(this._appUser?.userDivisonAccesses, (access) => access.divisionId, divisionId) &&
      this._hasAccessTo(this._appUser?.userProductGroupAccesses, (access) => access.productGroupId, productGroupId)
    );
  }

  private _hasAccessTo<T>(
    accesses: T[] | null | undefined,
    accessId: (access: T) => number | null | undefined,
    requestedId?: number | null
  ): boolean {
    if (requestedId === undefined || requestedId === null) {
      return true;
    }

    return (accesses ?? []).some((access) => {
      const id = accessId(access);
      return id === undefined || id === null || id === requestedId;
    });
  }

  async loadAppUser(): Promise<UserDTO | null> {
    const account = this._msalAuthService.instance.getActiveAccount();
    const objectId = account
      ? account.localAccountId || (account.idTokenClaims?.['oid'] as string | undefined)
      : undefined;

    if (!objectId) {
      this._clearAppUser();
      return null;
    }

    if (this._appUserObjectId === objectId) {
      // Already loaded for this account, or a load for it is already in flight (e.g.
      // checkAndSetActiveAccount() and initializeAuthentication() both call loadAppUser() for the
      // same account without awaiting each other) - await that same request instead of returning
      // possibly-stale (still null) data immediately.
      return this._loadAppUserPromise ?? this._appUser;
    }

    this._appUserObjectId = objectId;

    this._loadAppUserPromise = (async () => {
      try {
        const user = await firstValueFrom(this._userService.apiUsersUserbyobjectidGet(objectId));
        this._setAppUser(user ?? null);
      } catch {
        this._appUserObjectId = null;
        this._setAppUser(null);
      } finally {
        this._loadAppUserPromise = null;
      }
      return this._appUser;
    })();

    return this._loadAppUserPromise;
  }

  async reloadAppUser(): Promise<UserDTO | null> {
    this._appUserObjectId = null;
    return this.loadAppUser();
  }

  private async _startListeningForUpdates(): Promise<void> {
    if (!this.isAuthenticated() || this._listenersRegistered) {
      return;
    }
    this._listenersRegistered = true;

    try {
      await this._signalR.startConnection();
    } catch (error) {
      console.error('Failed to start SignalR connection', error);
      this._listenersRegistered = false;
      return;
    }

    this._signalR.on<void>('UserUpdated').subscribe(() => {
      void this.reloadAppUser();
    });
  }

  private _setAppUser(user: UserDTO | null) {
    this._appUser = user;
    this._appUser$.next(user);
  }

  private _clearAppUser() {
    this._appUserObjectId = null;
    this._setAppUser(null);
  }
}
